import React, { useEffect, useState } from "react";
import type { StatisticProps } from "antd";
import { Select, Skeleton, Space, Statistic } from "antd";
import CountUp from "react-countup";
import { Card } from "antd";
import { Line } from "react-chartjs-2";
import { Chart as ChartJS, registerables } from "chart.js";
import { useAccount } from "src/hooks/account.hook";
import { AssetResponse, Balance } from "src/models/account";
import { getChartsData, intervals } from "src/data/account";
import { getChartsOptions } from "src/data/columns";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "src/hooks/analytics.hook";
import { accountAtom } from "src/store";
import { useAtom } from "jotai";
import { usePageTracking } from "src/hooks/tracking-page.hook";
const formatter: StatisticProps["formatter"] = (value) => (
  <CountUp end={value as number} separator=" " suffix=" FCFA " />
);

export default function Investments() {
  ChartJS.register(...registerables);
  const [balance, setBalance] = useState<Balance>({});

  const [data, setData] = useState<any>(getChartsData());

  const [options, setOptions] = useState<any>(getChartsOptions());

  const [balanceLoading, setBalanceLoading] = useState<boolean>(false);

  const [assetsLoading, setAssetsLoading] = useState<boolean>(false);

  const [interval, setInterval] = useState<string>("1 year");

  const { getUserBalance, getAssetEvolution } = useAccount();
  const { trackPage } = usePageTracking();

  const [account] = useAtom(accountAtom);

  const { track } = useAnalytics();
  const { t } = useTranslation();
  useEffect(() => {    
    trackPage();
  }, []);
  
  useEffect(() => {
    getBalance();
    getAssets();
  }, [account]);

  const getBalance = () => {
    setBalanceLoading(true);
    setBalance({});
    getUserBalance({ userId: account?.id })
      .then((value) => setBalance(value))
      .finally(() => setBalanceLoading(false));
  };

  const getAssets = (data?: string) => {
    setAssetsLoading(true);
    setData({});
    getAssetEvolution({ interval: data || interval, userId: account?.id })
      .then((value: any[]) => {
        if (value) {
          setData(
            getChartsData(
              value?.map((elt: AssetResponse) => elt?.date_current),
              value?.map((elt: AssetResponse) => elt?.capital_balance)
            )
          );
          setOptions(
            getChartsOptions(
              value?.map((elt: AssetResponse) => elt?.unrealised_interest)
            )
          );
        }
      })
      .finally(() => setAssetsLoading(false));
  };
  const handleChange = (value: string) => {
    setInterval(value);
    getAssets(value);
    track("Setting", {
      page_current: "my_investments",
      element_type: "drop-down",
      setting_name: "time range",
      value_set: value,
      setting_affects: "page",
    });
  };

  return (
    <div>
      <Card hoverable className="flex justify-start items-start bg-op">
        <div className="flex justify-start w-full flex-col md:flex-row">
          {balanceLoading ? (
            <>
              <Skeleton avatar paragraph={{ rows: 2 }} />
              <Skeleton avatar paragraph={{ rows: 2 }} />
            </>
          ) : (
            <>
              <div className="flex justify-start items-center mr-5 mb-2">
                <img
                  src="/icons/capitale.png"
                  alt="wallet.png"
                  className="mr-2 h-[50px] w-[50px] md:h-[70px] md:w-[70px]"
                />
                <Statistic
                  title={t("capitalBalance")}
                  value={balance?.capital_balance || 0}
                  precision={2}
                  formatter={formatter}
                />
              </div>
              <div className="flex justify-start items-center">
                <img
                  src="/icons/wallet.png"
                  alt="wallet.png"
                  className="mr-2 h-[50px] w-[50px] md:h-[70px] md:w-[70px]"
                />
                <Statistic
                  title={t("unrealisedInterest")}
                  value={balance?.unrealised_interest || 0}
                  precision={2}
                  formatter={formatter}
                />
              </div>
            </>
          )}
        </div>
      </Card>

      <Card hoverable className="flex justify-center items-center mt-4 bg-op">
        <h2 className="text-center text-lg lg:text-2xl font-bold mr-2">
          {t("assetsEvolutions")}
        </h2>
        <div className="flex justify-between w-full">
          <Space wrap className="mt-5">
            <h2 className="text-center text-md lg:text-2xl font-bold">
              {t("timeRange.label")}
            </h2>
            <Select
              value={interval}
              disabled={assetsLoading}
              style={{ width: 120 }}
              onChange={handleChange}
              options={intervals.map((elt) => ({
                value: elt.value,
                label: t(elt.label),
              }))}
            />
          </Space>
        </div>
        <div className="w-full flex justify-center min-h-[100px] h-[40vh] lg:h-[60vh] mt-3">
          <Line options={options} data={data} className="w-full h-full" />
        </div>
      </Card>
    </div>
  );
}
